import React, { useCallback } from 'react'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { post } from 'aws-amplify/api'
import { getCachedAuthSession } from 'services/authSessionCache'
import styled from 'styled-components'

import { color } from 'styles/colors'

import { CitationFile } from 'types/Chat'

type Props = {
  files: CitationFile[],
  isSourceExpanded: boolean,
  onToggleSourceCallback: () => void
}
const SourceMaterialFilesList = ({ files, isSourceExpanded, onToggleSourceCallback }: Props) => {
  const [downloadingIndexes, setDownloadingIndexes] = React.useState<Set<number>>(new Set())

  const downloadFile = useCallback(async (s3Uri: string, index: number, cacheTitle: string | undefined) => {
    if (downloadingIndexes.has(index)) return

    setDownloadingIndexes((prev) => new Set(prev).add(index))

    try {
      const session = await getCachedAuthSession()
      const idToken = session.tokens?.idToken?.toString()

      if (!idToken) {
        throw new Error('No auth token available')
      }

      const res = await post({
        apiName: 'technicianBotApi',
        path: '/1/citation/download-url',
        options: {
          headers: {
            Authorization: idToken,
            'Content-Type': 'application/json'
          },
          body: { s3Uri }
        }
      }).response

      const data = await res.body.json() as { presignedUrl?: string }

      if (data?.presignedUrl) {
        const anchor = document.createElement('a')
        anchor.href = data.presignedUrl
        anchor.download = `${cacheTitle ?? 'file'}.pdf`
        anchor.click()
      }
    } catch (error) {
      console.error('Failed to download file:', error)
    } finally {
      setDownloadingIndexes((prev) => {
        const next = new Set(prev)
        next.delete(index)
        return next
      })
    }
  }, [downloadingIndexes])

  return (
    <S.Container>
      <S.Header onClick={onToggleSourceCallback}>
        Source Material
        {isSourceExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
      </S.Header>
      <S.ListContainer $expanded={isSourceExpanded} aria-hidden={!isSourceExpanded} data-testid="source-material-list">
        {files.map((file, index) => (
          <S.List
            key={file.s3Uri}
            onClick={() => file.s3Uri && downloadFile(file.s3Uri, index, file.cacheTitle)}
            $disabled={downloadingIndexes.has(index)}
          >
            <img src="/img/pdf.svg" alt="pdf icons" style={{ marginRight: 8 }} />
            {downloadingIndexes.has(index) ? 'Downloading...' : file.cacheTitle}
          </S.List>
        ))}
      </S.ListContainer>
    </S.Container>
  )
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    border-top: 1px solid ${color('divider')};
  `,
  Header: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    padding: 8px;
    color: ${color('secondaryText')};
    margin-top: 16px;
    cursor: pointer;
  `,
  ListContainer: styled.div<{ $expanded: boolean }>`
    overflow: auto;
    max-height: ${({ $expanded }) => ($expanded ? '300px' : '0')};
    transition: max-height 0.3s ease;
  `,
  List: styled.div<{ $disabled?: boolean }>`
    padding: 4px 8px;
    width:  75%;
    font-size: 12px;
    border-radius: 6px;
    margin: auto;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    background-color: ${color('chatbotSourceListBackground')};
    color: ${color('anchor')};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  `
}
export default SourceMaterialFilesList
